import React, { useEffect, useState } from 'react';
import { MaskService } from 'react-native-masked-text';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { abuseReportService } from '../../../services/central-api/abuse-report';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { AccessParamList } from '../../../typings/Params';
import MyDataTable from '../../../components/MyDataTable';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import AbuseReportDetailModal from './AbuseReportDetailModal';
import { useAuth } from '../../../context/Auth';
import Container from '../../../components/Container';
import Card from '../../../components/Card';
import Header from '../../../components/Header';
import DataTableContainer from '../../../components/DataTableContainer';
import DataTableHeader from '../../../components/DataTableHeader';
import { useQuery } from '@tanstack/react-query';
import { RootNavigation } from '../../../Routes';

export default function AbuseReports({ navigation }: { navigation: RootNavigation; }) {
    const route = useRoute<RouteProp<AccessParamList, 'AbuseReports'>>();
    const { user: authenticatedUser } = useAuth();

    const [abuseReports, setAbuseReports] = useState<AbuseReport[]>([]);
    const [selectedAbuseReport, setSelectedAbuseReport] = useState<AbuseReport>();
    const [isDetailModalVisible, setDetailModalVisible] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [filters, setFilters] = useState({
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['accessAbuseReports', filters],
        queryFn: () => abuseReportService.getAbuseReports(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setAbuseReports(data.rows);
        setTotalItems(data.count);
    }, [data]);

    function getStatusText(status: AbuseReportStatus) {
        switch (status) {
            case 'pending':
                return translate('pending');
            case 'rejected':
                return translate('rejected');
            case 'resolved':
                return translate('resolved');
        }
    }

    return <Container>
        <Header title={translate('abuseReports')} />
        <Card>
            <DataTableHeader title={translate('registeredAbuseReports')} zIndex={2} />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: translate('name'),
                        selector: row => row.name ?? '-',
                        grow: 2,
                    },
                    {
                        name: translate('phone'),
                        selector: row => row.phone ? MaskService.toMask('cel-phone', row.phone, {
                            maskType: 'BRL',
                            withDDD: true,
                            dddMask: '(99) '
                        }) : '-',
                        grow: 2,
                    },
                    {
                        name: translate('status'),
                        selector: row => getStatusText(row.status),
                        grow: 2,
                    },
                    {
                        name: translate('reviewer'),
                        selector: row => row.reviewer ? `${row.reviewer.warName ? row.reviewer.warName : row.reviewer.name} - RF: ${row.reviewer.registry}` : '-',
                        grow: 2,
                    },
                    {
                        name: '',
                        button: true,
                        wrap: true,
                        cell: (row) => {
                            const actions: Action[] = [{
                                content: translate('view'),
                                onPress: () => {
                                    setSelectedAbuseReport(row);
                                    setDetailModalVisible(true);
                                },
                            }];

                            return (
                                <DropdownButton
                                    actions={actions}
                                />
                            );
                        }

                    }]}
                    data={abuseReports}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={totalItems}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                />}
            />
        </Card>

        <AbuseReportDetailModal
            isModalVisible={isDetailModalVisible}
            abuseReportId={selectedAbuseReport?.id}
            description={selectedAbuseReport?.description}
            commentary={selectedAbuseReport?.commentary}
            status={selectedAbuseReport?.status}
            authenticatedUser={authenticatedUser}
            onClose={() => {
                setDetailModalVisible(false);
                setSelectedAbuseReport(undefined);
                refetch();
            }}
        />

    </Container>;
}
